// ============================================================
// THE COHESION POOL (Squad Mode stage 6) - one per kill-team, in the
// existing `shared/cohesion` document (member-writable: any brother in
// Squad Mode spends it, ruled 2026-10-23), every transition a
// compare-and-set on the whole document:
//
//   { v: 1, pools: { [tag]: { pool, oathId?, running: Running[], entryWindow? } } }
//
// A pool is FILLED when the session first finds the kill-team without one
// (plan modes-rework: "full at the session's start" - a session has no start
// event, and the table begins each mission from a fresh one), at the start
// the sheets work out; `oathId` is the Oath it was filled for, so a new Oath
// on the sheets resets it. The GM's Refill puts it back to its start.
//
// A RUNNING ability carries a snapshot of the effects it started with, so a
// later edit to the Tactics item, or its deletion, never changes what is
// already in play. Each step here is pure: the next pool and the log lines
// it earns, or a sentence saying why not.
//
// A document that is not there, or reads as `{}`, is NOT LOADED, never an
// empty pool of 0 (gotcha 13): `readCohesionDoc` answers null for it.
//
// Pure. Zero React dependency.
// ============================================================
import type { StanceEffect } from '../character/effectItems';
import type { SquadAbility } from './tactics';

export interface Running {
  key: string;
  abilityId: string;
  name: string;
  /** The Tactics copy's version it started under: the popover marks an older one. */
  rev: number;
  effects: StanceEffect[];
  sustained: boolean;
  startedBy: string;
  sceneId: string | null;
  joiners: string[];
  at: number;
}

export interface Pool {
  pool: number;
  /** The Oath it was filled for: another on the sheets resets it (`reset`). Absent on an older session's pool. */
  oathId?: string;
  running: Running[];
  /** The errata's free join: open until the opener's next turn start. */
  entryWindow?: { by: string };
}

export interface CohesionDoc {
  v: 1;
  pools: Record<string, Pool>;
}

/** A step's outcome: the next pool and its lines, or why it was refused. */
export type Step = { next: Pool; lines: string[] } | { refused: string };

type Raw = Record<string, unknown>;

function readRunning(raw: unknown): Running | null {
  const r = (raw ?? null) as Raw | null;
  if (!r || typeof r.key !== 'string' || typeof r.abilityId !== 'string' || typeof r.startedBy !== 'string')
    return null;
  return {
    key: r.key,
    abilityId: r.abilityId,
    name: typeof r.name === 'string' ? r.name : r.abilityId,
    rev: typeof r.rev === 'number' ? r.rev : 0,
    effects: Array.isArray(r.effects) ? (r.effects as StanceEffect[]) : [],
    sustained: r.sustained === true,
    startedBy: r.startedBy,
    sceneId: typeof r.sceneId === 'string' ? r.sceneId : null,
    joiners: Array.isArray(r.joiners) ? r.joiners.filter((j): j is string => typeof j === 'string') : [],
    at: typeof r.at === 'number' ? r.at : 0,
  };
}

function readPool(raw: unknown): Pool | null {
  const r = (raw ?? null) as Raw | null;
  if (!r || typeof r.pool !== 'number' || !Number.isFinite(r.pool)) return null;
  const running = (Array.isArray(r.running) ? r.running : []).map(readRunning).filter((x): x is Running => x !== null);
  const by = (r.entryWindow as Raw | undefined)?.by;
  return {
    pool: Math.max(0, Math.round(r.pool)),
    ...(typeof r.oathId === 'string' ? { oathId: r.oathId } : {}),
    running,
    ...(typeof by === 'string' ? { entryWindow: { by } } : {}),
  };
}

/** The stored document, or null when it is not there or reads as nothing at all (gotcha 13: not loaded, not 0). */
export function readCohesionDoc(raw: unknown): CohesionDoc | null {
  const r = (raw ?? null) as Raw | null;
  if (!r || typeof r.pools !== 'object' || !r.pools) return null;
  const pools = Object.entries(r.pools as Raw).flatMap(([tag, p]) => {
    const pool = readPool(p);
    return pool ? [[tag, pool] as const] : [];
  });
  return { v: 1, pools: Object.fromEntries(pools) };
}

export const emptyCohesionDoc = (): CohesionDoc => ({ v: 1, pools: {} });

/** The document with one team's pool set. */
export const withPool = (doc: CohesionDoc, tag: string, pool: Pool): CohesionDoc => ({
  ...doc,
  pools: { ...doc.pools, [tag]: pool },
});

/** A fresh pool at its start, for an Oath: nothing running. */
export const freshPool = (start: number, oathId: string): Pool => ({ pool: Math.max(0, start), oathId, running: [] });

/** The GM's Refill: the pool back to its start (a new mission in the same session); what runs keeps running. */
export function refill(p: Pool, start: number): Step {
  const now = Math.max(0, start);
  return { next: { ...p, pool: now }, lines: [`Cohesion refilled: ${was(p, now)}`] };
}

const was = (p: Pool, now: number) => `Cohesion ${p.pool} → ${now}.`;

/** Spend `n` Cohesion for `why`; refused past what is left. */
export function spend(p: Pool, n: number, why: string): Step {
  if (n > p.pool) return { refused: `Not enough Cohesion: ${why} costs ${n}, ${p.pool} left.` };
  return { next: { ...p, pool: p.pool - n }, lines: [`${why}: ${was(p, p.pool - n)}`] };
}

interface Start {
  ability: SquadAbility;
  rev: number;
  startedBy: string;
  sceneId: string | null;
  at: number;
  key: string;
}

/** Start an ability: its cost spent, its effects snapshotted, its starter its first joiner. */
export function startAbility(p: Pool, s: Start): Step {
  const paid = spend(p, s.ability.cost, s.ability.name);
  if ('refused' in paid) return paid;
  const running: Running = {
    key: s.key,
    abilityId: s.ability.id,
    name: s.ability.name,
    rev: s.rev,
    effects: s.ability.effects,
    sustained: s.ability.sustained,
    startedBy: s.startedBy,
    sceneId: s.sceneId,
    joiners: [s.startedBy],
    at: s.at,
  };
  return { next: { ...paid.next, running: [...paid.next.running, running] }, lines: paid.lines };
}

/** Join a running ability: no action, no Cohesion; only on its own stage, and once. */
export function join(p: Pool, key: string, brotherId: string, sceneId: string | null): Step {
  const r = p.running.find((x) => x.key === key);
  if (!r) return { refused: 'That ability is no longer running.' };
  if (r.sceneId !== sceneId) return { refused: `${r.name} is running on another stage.` };
  if (r.joiners.includes(brotherId)) return { refused: `Already in ${r.name}.` };
  const next = p.running.map((x) => (x.key === key ? { ...x, joiners: [...x.joiners, brotherId] } : x));
  return { next: { ...p, running: next }, lines: [] };
}

/** Stop a running ability (anyone in Squad Mode, or the GM). */
export function stop(p: Pool, key: string): Step {
  const r = p.running.find((x) => x.key === key);
  if (!r) return { refused: 'That ability is no longer running.' };
  return { next: { ...p, running: p.running.filter((x) => x.key !== key) }, lines: [`${r.name} stopped.`] };
}

/** The GM's +/- (Fate Points and the like); never below zero. */
export function adjust(p: Pool, delta: number): Step {
  const now = Math.max(0, p.pool + Math.round(delta));
  return { next: { ...p, pool: now }, lines: [`The GM adjusts the pool: ${was(p, now)}`] };
}

/** A different Oath on the sheets: the pool back to its new start, and every ability it does not unlock ended. */
export function reset(p: Pool, start: number, oathId: string, unlocked: ReadonlySet<string>): Step {
  const ended = p.running.filter((r) => !unlocked.has(r.abilityId));
  const lines = [
    `A new Oath: ${was(p, Math.max(0, start))}`,
    ...ended.map((r) => `${r.name} ends: the Oath no longer has it.`),
  ];
  return {
    next: { ...p, pool: Math.max(0, start), oathId, running: p.running.filter((r) => unlocked.has(r.abilityId)) },
    lines,
  };
}

/** The free join opened by `by` (until their next turn start), or closed. */
export const withWindow = (p: Pool, by: string | null): Pool => {
  const { entryWindow: _was, ...rest } = p;
  return by ? { ...rest, entryWindow: { by } } : rest;
};

/** Emptied: every brother drops to Solo and nothing keeps running (the caller writes their states). */
export const isEmptied = (p: Pool): boolean => p.pool <= 0;

/** The running abilities a brother is in, for his effective sheet. */
export const joinedBy = (p: Pool, brotherId: string): Running[] =>
  p.running.filter((r) => r.joiners.includes(brotherId));
